import { CallStateEnum, Participant, ConferenceTypeEnum, Conference } from '@matap/shared';
import { Stream } from './stream';
import { P2PPeer } from './p2p.peer';
import { PeerLocalStream } from './peer.local.stream';
import { SDP } from './sdp';
import { DeviceManager } from './device-manager/device.manager';
import { PlatformType } from './utils/utils';

const TAG = '[webrtc-P2PRoom]';

export class P2PRoom extends Stream<'peers' | 'analytic' | 'state-change'> {
  public readonly _id: string;
  private readonly session: Conference;
  public readonly type: ConferenceTypeEnum;
  private readonly peers: P2PPeer[] = [];
  private readonly peerLocalStream: PeerLocalStream;
  private readonly deviceManager: DeviceManager;
  private incomingQueue: SDP[] = [];
  public readonly isInitiator: boolean;

  constructor(
    session: Conference,
    private configs: {
      userId: string;
    },
  ) {
    super();
    this._id = session._id;
    this.session = session;
    this.type = session.type;
    this.deviceManager = new DeviceManager(session._id);
    this.isInitiator = session.initiator.id === configs.userId;

    this.peerLocalStream = new PeerLocalStream(this.getMyMediaConstraints(), session._id, this.deviceManager);
    this.peerLocalStream.on('error', (error) => this.destroy(error)).on('analytic', (payload) => this.emit('analytic', payload));
    this.peerLocalStream.initialize();

    this.deviceManager.on('analytic', (payload) => this.emit('analytic', payload));
  }

  public async start() {
    const { session } = this;
    if (this.started || this.isDestroyedOrDestroying()) {
      return;
    }
    session.state = CallStateEnum.transmitting;
    this.started = true;

    // FIX 4: Only add the remote participant
    const remoteParticipant = session.initiator.id === this.configs.userId ? session.receiver : session.initiator;

    await this.addParticipant(remoteParticipant);
    this.deviceManager.startInCallService(this.type);
    this.emit('state-change', this.getState());
  }

  public getState() {
    return this.session.state;
  }

  public isStarted(): boolean {
    return this.started;
  }

  public getPeerLocalStream() {
    return this.peerLocalStream;
  }

  public getDeviceManager() {
    return this.deviceManager;
  }

  public getMyMediaConstraints() {
    const platform = PlatformType;

    // 1. Native Mobile App (React Native iOS/Android)
    if (platform === 'android' || platform === 'ios') {
      return this.session.mediaConstraints.native;
    }

    // 2. Mobile Web Browser (Safari/Chrome on Phone or Tablet)
    if (platform === 'android-web' || platform === 'ios-web') {
      return this.session.mediaConstraints.mobileWeb;
    }

    // 3. Desktop Web Browser (PC / Mac fallback)
    return this.session.mediaConstraints.desktopWeb;
  }

  public onSDP(sdp: SDP) {
    if (this.isDestroyedOrDestroying()) {
      return;
    }
    const peer = this.getPeer(sdp.fromUserId);
    if (peer) {
      peer.onSignal(sdp);
    } else {
      this.incomingQueue.push(sdp);
    }
  }

  public getPeers() {
    return this.peers;
  }

  public getPeer(userId: string) {
    return this.peers.find((peer) => peer.userId === userId);
  }

  private async addParticipant(participant: Participant) {
    try {
      const { session, peerLocalStream, configs } = this;
      if (participant.id === this.configs.userId) {
        return;
      }
      await peerLocalStream.initialize();
      let peer: P2PPeer | undefined = this.peers.find((p) => p.userId === participant.id);
      if (peer) {
        return;
      }

      peer = new P2PPeer(peerLocalStream, {
        userId: participant.id,
        conferenceId: session._id,
        mediaConstraints: this.getMyMediaConstraints(),
        trickleIce: session.trickleIce,
        iceServers: session.iceServers,
        iceTransportPolicy: session.iceTransportPolicy,
        type: session.type,
        myUserId: configs.userId,
        initiatorId: session.initiator.id,
        videoMaxBitrate: session.videoMaxBitrate,
        audioMaxBitrate: session.audioMaxBitrate,
        preferredCodecs: session.preferredCodecs,
        version: session.videoCallVersion,
      });
      this.peers.push(peer);
      peer
        .on('analytic', (payload) => this.emit('analytic', payload))
        .on('close', (args) => this.destroy(args))
        .start();

      this.emit('peers', this.getPeers());
      this.incomingQueue = this.incomingQueue.filter((sdp) => {
        if (sdp.fromUserId === participant.id) {
          peer?.onSignal(sdp);
          return false;
        }
        return true;
      });
    } catch (e: any) {
      console.error(e);
      console.error(TAG, 'error when creating participant', e);
    }
  }

  protected onDestroy() {
    this.peerLocalStream.destroy();

    this.peers.forEach((peer) => peer.destroy());
    this.peers.length = 0;

    this.deviceManager.destroy();

    /* this.cleanupGlobalAudio();*/
  }

  /*  private stacleanupGlobalAudio() {
    const audio = (global as any).audio;
    if (audio) {
      try {
        if (typeof audio.disconnect === 'function') audio.disconnect();
        else if (typeof audio.pause === 'function') audio.pause();
      } catch (e) {
        console.warn(TAG, 'Failed to cleanup global audio', e);
      }
      (global as any).audio = undefined;
    }
  }*/
}
